/**
 * Slider arithmetic, extracted from `@salt-ds/core` and now ours.
 *
 * These are pure functions on purpose. Everything that makes a slider hard — drag, focus,
 * tooltips, two thumbs that must not cross — reduces to "what value does this position mean"
 * and "what value does this key mean", and both are testable without a DOM.
 */

export interface SliderMark {
  label: string;
  value: number;
}

export const toFloat = (value: number | string): number =>
  typeof value === "string" ? Number.parseFloat(value) : value;

/** Where a value sits along the track, 0–100. */
export const calculatePercentage = (value: number, max: number, min: number): number =>
  ((value - min) / (max - min)) * 100;

/**
 * Mark positions are clamped and rounded to 2dp, while `calculatePercentage` is neither.
 *
 * That asymmetry is deliberate, not an oversight to tidy: a mark is a static decoration whose
 * position is written into a `left:` style, so an unrounded float there produces a long
 * meaningless string in the DOM. A progress percentage is recomputed every frame during a drag,
 * where rounding would make the fill visibly stutter against the thumb.
 */
export const calculateMarkPosition = (value: number, max: number, min: number): number => {
  if (min === max) return 0;
  const clamped = Number.isNaN(toFloat(value)) ? min : Math.min(Math.max(toFloat(value), min), max);
  return Math.round(((clamped - min) / (max - min)) * 100 * 100) / 100;
};

/**
 * Bring a value onto the track: inside [min, max], onto the step grid, and — when
 * `restrictToMarks` — onto the nearest mark.
 *
 * `restrictToMarks` IGNORES `step` entirely rather than combining with it. Two competing grids
 * would let a value be legal on one and illegal on the other, and there is no sensible answer
 * for where the thumb then lands.
 */
export const clamp = (
  value: number,
  max: number,
  min: number,
  step: number,
  decimalPlaces: number,
  marks?: SliderMark[],
  restrictToMarks?: boolean,
): number => {
  if (Number.isNaN(value)) return min;
  const clamped = Math.min(Math.max(value, min), max);

  if (restrictToMarks && marks) {
    let closest = marks[0].value;
    let smallest = Math.abs(clamped - closest);
    for (let i = 1; i < marks.length; i++) {
      const difference = Math.abs(clamped - marks[i].value);
      if (difference < smallest) {
        smallest = difference;
        closest = marks[i].value;
      }
    }
    return closest;
  }

  let rounded = Math.round(clamped / step) * step;
  // Rounding to the step grid can push a value back OUT of range — min=0 max=95 step=10 rounds
  // 95 up to 100. Re-clamping after the snap is what keeps the thumb on the track.
  if (rounded > max) rounded = max;
  else if (rounded < min) rounded = min;

  // Floating-point step arithmetic produces 0.30000000000000004. `decimalPlaces` is what stops
  // that reaching the value a caller reads back.
  return Number.parseFloat(rounded.toFixed(decimalPlaces));
};

/** Clamp both ends of a range, swapping them first if the caller passed them backwards. */
export const clampRange = (
  range: [number, number],
  max: number,
  min: number,
  step: number,
  decimalPlaces: number,
  marks?: SliderMark[],
  restrictToMarks?: boolean,
): [number, number] => {
  let [start, end] = range;
  if (Number.isNaN(start)) start = min;
  if (Number.isNaN(end)) end = max;
  if (start > end) [start, end] = [end, start];
  return [
    clamp(start, max, min, step, decimalPlaces, marks, restrictToMarks),
    clamp(end, max, min, step, decimalPlaces, marks, restrictToMarks),
  ];
};

/** Translate a pointer's x into a slider value. Returns undefined before the track has a box. */
export const getClickedPosition = (
  sliderRef: React.RefObject<HTMLDivElement>,
  clientX: number,
  max: number,
  min: number,
  step: number,
  decimalPlaces: number,
  marks?: SliderMark[],
  restrictToMarks?: boolean,
): number | undefined => {
  if (!sliderRef.current) return undefined;
  const rect = sliderRef.current.getBoundingClientRect();
  const raw = ((clientX - rect.left) / rect.width) * (max - min) + min;
  return clamp(Math.round(raw / step) * step, max, min, step, decimalPlaces, marks, restrictToMarks);
};

/**
 * Translate a key into a value.
 *
 * `preventDefault` fires ONLY for keys this function handled, and that placement is the whole
 * trick: the visible thumb is a native `<input type="range">`, which moves itself on arrow keys.
 * Preventing the default is what stops the value being applied twice — once by us and once by
 * the browser. Return early WITHOUT preventing for anything else, or Tab stops working.
 */
export const getKeyboardValue = (
  event: React.KeyboardEvent,
  value: number,
  step: number,
  stepMultiplier: number,
  max: number,
  min: number,
  restrictToMarks?: boolean,
  marks?: SliderMark[],
): number => {
  let newValue = value;

  if (restrictToMarks && marks && marks.length >= 1) {
    // Stepping mark-to-mark by INDEX, not by value: marks may be unevenly spaced, and a user
    // pressing ArrowRight means "the next mark", not "the next number".
    const currentIndex = marks.findIndex((mark) => mark.value === value);
    switch (event.key) {
      case "ArrowUp":
      case "ArrowRight":
      case "PageUp":
        if (currentIndex < marks.length - 1) newValue = marks[currentIndex + 1].value;
        break;
      case "ArrowDown":
      case "ArrowLeft":
      case "PageDown":
        if (currentIndex > 0) newValue = marks[currentIndex - 1].value;
        break;
      default:
        return newValue;
    }
  } else {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowRight":
        newValue = Math.min(value + step, max);
        break;
      case "ArrowDown":
      case "ArrowLeft":
        newValue = Math.max(value - step, min);
        break;
      case "Home":
        newValue = min;
        break;
      case "End":
        newValue = max;
        break;
      case "PageUp":
        newValue = Math.min(value + step * stepMultiplier, max);
        break;
      case "PageDown":
        newValue = Math.max(value - step * stepMultiplier, min);
        break;
      default:
        return newValue;
    }
  }

  event.preventDefault();
  return newValue;
};
